import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { useTour } from './TourProvider';
import TourSpotlight from './TourSpotlight';
import TourTooltip from './TourTooltip';

const ANCHOR_TIMEOUT_MS = 3000;

export default function TourOverlay() {
  const { t } = useTranslation();
  const { activeTour, stepIndex, advance, skip } = useTour();
  const [targetEl, setTargetEl] = useState(null);
  const [anchorMissing, setAnchorMissing] = useState(false);

  const step = activeTour?.steps?.[stepIndex];

  // Resolve anchor target via DOM (data-tour-anchor attribute)
  useEffect(() => {
    if (!step) { setTargetEl(null); setAnchorMissing(false); return; }
    if (!step.anchor) { setTargetEl(null); setAnchorMissing(false); return; }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = ANCHOR_TIMEOUT_MS / 100;

    const find = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-tour-anchor="${step.anchor}"]`);
      if (el) { setTargetEl(el); setAnchorMissing(false); return; }
      attempts += 1;
      if (attempts > maxAttempts) { setAnchorMissing(true); setTargetEl(null); return; }
      setTimeout(find, 100);
    };
    setTargetEl(null);
    setAnchorMissing(false);
    find();

    return () => { cancelled = true; };
  }, [step?.anchor, stepIndex]);

  // Auto-advance on click of click-gated anchor
  useEffect(() => {
    if (!step || step.gate.type !== 'click' || !targetEl) return;
    const onClick = () => advance();
    targetEl.addEventListener('click', onClick, { once: true });
    return () => targetEl.removeEventListener('click', onClick);
  }, [step, targetEl, advance]);

  if (!activeTour || !step) return null;
  if (anchorMissing) return null; // tour effectively pauses

  // End-card stops are full-screen modals (no anchor, no spotlight)
  if (step.isEndCard) {
    const actions = step.endCardActions;
    return (
      <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl p-6 max-w-md w-full">
          <div className="text-lg font-semibold text-white mb-2">{t(`${step.copyKey}.title`, '')}</div>
          <div className="text-sm text-zinc-300 mb-4 leading-relaxed">{t(`${step.copyKey}.body`, '')}</div>
          <div className="flex flex-col gap-2">
            {actions ? actions.map((action) => (
              <Button
                key={action.id}
                onClick={() => action.handler({ advance, skip })}
                variant={action.variant || 'default'}
                className={action.className || 'bg-orange-500 hover:bg-orange-600 text-white'}
              >
                {t(action.labelKey)}
              </Button>
            )) : (
              <Button onClick={advance} className="bg-orange-500 hover:bg-orange-600 text-white">
                {t('tour.common.done')}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <TourSpotlight targetEl={targetEl} />
      {targetEl && (
        <TourTooltip
          targetEl={targetEl}
          copyKey={step.copyKey}
          onNext={advance}
          onSkip={skip}
          showSkip={!!step.showSkip}
          gateType={step.gate.type}
          isFinalStep={stepIndex === activeTour.steps.length - 1}
        />
      )}
    </>
  );
}
